/**
 * What the master pane is showing: rows, or which of the five kinds of nothing.
 *
 * ── One decision, two readers, and they used to be written out twice ─────────────────────────
 * The screen branched five ways to pick what stands in the list card, and then branched the same
 * three inputs again — in a different order — to tell the inspector why it has no plan to describe.
 * They agreed because somebody kept them in step by hand; nothing made them agree. This file is the
 * decision, `ui/ListStates` draws one half of it and `ui/InspectorEmpty` the other, and the
 * inspector's own vocabulary is now this union minus its one populated member.
 *
 * ── The order of the questions is the whole rule ─────────────────────────────────────────────
 * A read that has not answered is neither empty nor filtered, so the read's own status comes first
 * — `rows.length === 0` cannot tell a request still running from one that answered with nothing.
 *
 * Then the SCOPE's own emptiness, before the filter's: a trainer with no programmes who has typed
 * something in the search box has no programmes, and telling them their query matched nothing would
 * send them to clear a filter that is not what is standing in their way. `noPlans` and
 * `noClientPlans` are two different facts with two different ways out — one is the builder, the
 * other is the roster — which is why they are two members rather than one with a scope beside it.
 *
 * Only then "nothing matched": the scope holds rows, and none of them survived. That is the one
 * thing a narrowing can do, so it needs no fourth input to say so.
 *
 * Pure. No React, no DOM, no i18n, no query hook.
 */

import type { PlanScope } from './planScope';

/**
 * The five absences plus the one populated state.
 *
 * `rows` is the only member that is not an absence, which is what makes `InspectorAbsence` an
 * `Exclude` rather than a second list somebody has to keep in step with this one.
 */
export type PlanListStatus =
  | 'pending'
  | 'error'
  | 'noPlans'
  | 'noClientPlans'
  | 'filtered'
  | 'rows';

/**
 * Why the inspector has no plan to describe.
 *
 * The list's own status, minus the case where it has rows — because a list with rows always has a
 * row for the pane to be pointed at.
 */
export type InspectorAbsence = Exclude<PlanListStatus, 'rows'>;

export interface PlanListReading {
  /** THIS scope's read. Three states, never a branch on the data. */
  readonly read: 'loading' | 'error' | 'ready';
  readonly scope: PlanScope;
  /** How many rows the scope holds before the search box and the facets narrow it. */
  readonly totalCount: number;
  /** How many survive them. */
  readonly rowCount: number;
}

/**
 * The same status as the pane beside the list reads it.
 *
 * `rows` is the one member that is not an absence, and it cannot reach the pane: a list with rows
 * always has a selection, because `usePointedRow` falls back to its first visible row. It maps to
 * the wait rather than to any of the three sentences — the wait draws the pane's own shape and says
 * nothing about the trainer's account, so it is the one answer that is wrong about nothing.
 */
export function inspectorAbsenceOf(status: PlanListStatus): InspectorAbsence {
  return status === 'rows' ? 'pending' : status;
}

export function planListStatus(reading: PlanListReading): PlanListStatus {
  if (reading.read === 'loading') return 'pending';
  if (reading.read === 'error') return 'error';
  if (reading.totalCount === 0) {
    return reading.scope === 'clients' ? 'noClientPlans' : 'noPlans';
  }
  return reading.rowCount === 0 ? 'filtered' : 'rows';
}
